import { RecordCollaborationPanel } from "../../../../collaboration/RecordCollaborationPanel";
import { useReportVariableMutations } from "../../../../collaboration/useReportVariableMutations";
import { Fragment, useEffect, useState, useMemo, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import {
  PlusCircleIcon,
  TrashIcon,
  PencilIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { PMField } from "../../../utils/PMField";
import { VariableTypeSelector } from "../../../utils/moduleSelectors/variableTypeSelector";
import { UnitCustomSelector } from "../../../utils/moduleSelectors/unitCustomSelector";
import ProConfirmation from "../../../utils/ProConfirmation";
import BulkAddVariablesDialog from "./bulk-add-variables-dialog";
import VariableMeasurementDialog from "./variable-measurement-dialog";
import VariableSketchEditorDialog from "./variable-sketch-editor-dialog";
import {
  RV_DIALOG_Z,
  RV_OVERLAY_CLASS,
  RV_CENTER_WRAPPER,
  RV_CENTER_INNER,
  rvTransitionOverlay,
  rvTransitionPanel,
  RV_PANEL_MD,
  RV_PANEL_LG,
  RV_DIALOG_TITLE,
  RV_BTN_OUTLINE,
  RV_BTN_PRIMARY_DARK,
  RV_INPUT,
  RV_SECTION,
  SKETCH_TOTAL_DOT_COLORS,
  RV_SELECTOR_OVER_DIALOG_Z,
} from "./report-variable-dialog-theme";
import { ReportVariables } from "./report-variables";
import {
  fetchReportVariables,
  fetchReportVariableGroups,
  fetchReportVariableTypeGroups,
  fetchReportVariableMeasurements,
} from "../../../../api/service/report-variables";
import {
  formatUnitDisplay,
  formatFlowPairNb,
  getFlowRateUnitKind,
} from "../../../../utils/reportVariableUnits";
import {
  isLegacyStyleReportVariable,
  reportUsesLegacyVariables,
} from "../../../../utils/reportVariablesUi";

/** ~44px+ touch targets on small screens for Variables V2 */
const iconBtnBase =
  "inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/25";
const iconBtn =
  `${iconBtnBase} h-11 w-11 text-current hover:bg-gray-100 active:bg-gray-200 sm:h-9 sm:w-9 sm:hover:bg-gray-50`;
const iconBtnDanger =
  `${iconBtnBase} h-11 w-11 text-red-600 hover:bg-red-50 active:bg-red-100 sm:h-9 sm:w-9`;
const iconBtnAccent =
  `${iconBtnBase} h-11 w-11 text-gray-700 hover:bg-gray-100 active:bg-gray-200 sm:h-9 sm:w-9`;
const iconBtnGreen =
  `${iconBtnBase} h-11 w-11 text-green-600 hover:bg-green-50 active:bg-green-100 sm:h-9 sm:w-9`;

/** Normalize API variable type for UI (camelCase/snake_case display title). */
function normalizeVariableType(variableType) {
  if (!variableType || typeof variableType !== "object") return null;
  const source = variableType.object && typeof variableType.object === "object"
    ? variableType.object
    : variableType;
  const displayTitle =
    source.displayTitle ||
    source.display_title ||
    variableType.primary ||
    source.title ||
    "";
  return {
    ...source,
    displayTitle: String(displayTitle).trim() || source.title || "",
  };
}

/** Variable type label from API (camelCase/snake_case) or type group fallback title. */
function getTypeGroupVariableTypeTitle(typeGroup) {
  if (!typeGroup) return "";
  const vt = typeGroup.report_variable_type;
  if (vt && typeof vt === "object") {
    const fromVt =
      vt.displayTitle ||
      vt.display_title ||
      vt.title ||
      "";
    if (fromVt) return String(fromVt).trim();
  }
  if (typeGroup.title != null && String(typeGroup.title).trim() !== "") {
    return String(typeGroup.title).trim();
  }
  return "";
}

/**
 * Aggregate sums by report_variable_type_id across all type groups / variables on the report.
 */
function aggregateTotalsByReportVariableTypeId(variables, variableTypeGroups) {
  const tgById = new Map(variableTypeGroups.map((tg) => [tg.id, tg]));
  const map = new Map();
  for (const v of variables) {
    if (!v.type_group_id) continue;
    const tg = tgById.get(v.type_group_id);
    if (!tg?.report_variable_type_id) continue;
    const rid = tg.report_variable_type_id;
    let e = map.get(rid);
    if (!e) {
      e = {
        refTg: tg,
        projected: 0,
        pre: 0,
        measured: 0,
        hasProj: false,
        hasPre: false,
        hasMeas: false,
      };
      map.set(rid, e);
    }
    if (v.projected_quantity != null && String(v.projected_quantity).trim() !== "") {
      const pq = parseFloat(v.projected_quantity);
      if (!Number.isNaN(pq)) {
        e.projected += pq;
        e.hasProj = true;
      }
    }
    if (v.pre_measurement_quantity != null && String(v.pre_measurement_quantity).trim() !== "") {
      const pr = parseFloat(v.pre_measurement_quantity);
      if (!Number.isNaN(pr)) {
        e.pre += pr;
        e.hasPre = true;
      }
    }
    const latest = v.measurements?.[0];
    if (latest != null && latest.value != null && String(latest.value).trim() !== "") {
      const mv = parseFloat(latest.value);
      if (!Number.isNaN(mv)) {
        e.measured += mv;
        e.hasMeas = true;
      }
    }
  }
  return map;
}

function formatNbNo2(n) {
  if (n == null || Number.isNaN(n)) return "";
  return new Intl.NumberFormat("nb-NO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Normaliser felt til l/s for tabell-sammenligning når enheten er luftmengde. */
function variableFieldAsLs(typeGroup, variable, field) {
  const projUnit = typeGroup.projected_unit || typeGroup.measurement_unit;
  const measUnit = typeGroup.measurement_unit;
  const pk = getFlowRateUnitKind(projUnit);
  const mk = getFlowRateUnitKind(measUnit);
  if (field === "proj") {
    if (variable.projected_quantity == null || String(variable.projected_quantity).trim() === "") return null;
    const v = parseFloat(variable.projected_quantity);
    if (Number.isNaN(v)) return null;
    if (pk === "m3h") return v / 3.6;
    if (pk === "ls") return v;
    return v;
  }
  if (field === "pre") {
    if (variable.pre_measurement_quantity == null || String(variable.pre_measurement_quantity).trim() === "") return null;
    const v = parseFloat(variable.pre_measurement_quantity);
    if (Number.isNaN(v)) return null;
    if (mk === "m3h") return v / 3.6;
    if (mk === "ls") return v;
    return v;
  }
  if (field === "meas") {
    const latest = variable.measurements?.[0];
    if (!latest || latest.value == null || String(latest.value).trim() === "") return null;
    const v = parseFloat(latest.value);
    if (Number.isNaN(v)) return null;
    if (mk === "m3h") return v / 3.6;
    if (mk === "ls") return v;
    return v;
  }
  return null;
}

function parseVariableQuantity(value) {
  if (value == null || String(value).trim() === "") return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

function sketchVariableStatusMeta(typeGroup, variable, t) {
  const mk = getFlowRateUnitKind(typeGroup.measurement_unit);
  const pk = getFlowRateUnitKind(typeGroup.projected_unit || typeGroup.measurement_unit);
  const lsProj = mk || pk ? variableFieldAsLs(typeGroup, variable, "proj") : parseVariableQuantity(variable.projected_quantity);
  const lsMeas = mk || pk
    ? variableFieldAsLs(typeGroup, variable, "meas")
    : parseVariableQuantity(variable.measurements?.[0]?.value);
  if (lsMeas == null) {
    return {
      key: "none",
      label: "—",
      dotClass: "bg-gray-400",
      pillClass: "bg-gray-100 text-gray-600",
    };
  }
  if (lsProj == null || lsProj === 0) {
    return {
      key: "ok",
      label: t("report.variables.sketch.statusMeasuredOnly", { defaultValue: "Målt" }),
      dotClass: "bg-slate-500",
      pillClass: "bg-slate-100 text-slate-700",
    };
  }
  const dev = Math.abs(lsMeas - lsProj) / Math.max(Math.abs(lsProj), 1e-9);
  if (dev <= 0.15) {
    return {
      key: "good",
      label: t("report.variables.sketch.statusGood", { defaultValue: "God" }),
      dotClass: "bg-emerald-500",
      pillClass: "bg-emerald-200 text-emerald-900",
    };
  }
  return {
    key: "warn",
    label: t("report.variables.sketch.statusCheck", { defaultValue: "Sjekk" }),
    dotClass: "bg-amber-500",
    pillClass: "bg-amber-100 text-amber-800",
  };
}

function variableHasPre(variable) {
  return (
    variable?.pre_measurement_quantity != null &&
    String(variable.pre_measurement_quantity).trim() !== ""
  );
}

function sketchVariableRowCells(tg, v) {
  const pu = tg.projected_unit || tg.measurement_unit;
  const mu = tg.measurement_unit;
  const pk = getFlowRateUnitKind(pu);
  const mk = getFlowRateUnitKind(mu);
  const projLs = variableFieldAsLs(tg, v, "proj");
  const preLs = variableFieldAsLs(tg, v, "pre");
  const measLs = variableFieldAsLs(tg, v, "meas");
  const projCell =
    projLs != null && (pk === "m3h" || pk === "ls")
      ? formatNbNo2(projLs)
      : v.projected_quantity != null && String(v.projected_quantity).trim() !== ""
        ? formatUnitDisplay(pu, v.projected_quantity)
        : "—";
  const preCell =
    preLs != null && (mk === "m3h" || mk === "ls")
      ? formatNbNo2(preLs)
      : v.pre_measurement_quantity != null && String(v.pre_measurement_quantity).trim() !== ""
        ? formatUnitDisplay(mu, v.pre_measurement_quantity)
        : "—";
  const measCell =
    measLs != null && (mk === "m3h" || mk === "ls")
      ? formatNbNo2(measLs)
      : v.measurements?.[0] != null &&
          v.measurements[0].value != null &&
          String(v.measurements[0].value).trim() !== ""
        ? formatUnitDisplay(mu, v.measurements[0].value)
        : "—";
  return { projCell, preCell, measCell };
}

/** Brand fills — strong enough that the whole row reads as green / yellow. */
const GOOD_ROW_BG = "#BBF7D0";
const CHECK_ROW_BG = "#FDE68A";

function isSketchVariableGood(status) {
  return status?.key === "good";
}

function isSketchVariableCheck(status) {
  return status?.key === "warn";
}

function sketchVariableRowBg(status) {
  if (isSketchVariableGood(status)) return GOOD_ROW_BG;
  if (isSketchVariableCheck(status)) return CHECK_ROW_BG;
  return undefined;
}

function sketchVariableRowClass(status) {
  if (sketchVariableRowBg(status)) {
    return "cursor-pointer";
  }
  return "cursor-pointer bg-white hover:bg-gray-50";
}

function sketchVariableCellClass(status, extra = "") {
  const tone = sketchVariableRowBg(status) ? "" : "bg-white group-hover:bg-gray-50";
  return `${tone} ${extra}`.trim();
}

function sketchVariableRowStyle(status) {
  const backgroundColor = sketchVariableRowBg(status);
  return backgroundColor ? { backgroundColor } : undefined;
}

function sketchVariableAccentClass(status) {
  if (isSketchVariableGood(status)) return "border-l-4 border-emerald-600";
  if (isSketchVariableCheck(status)) return "border-l-4 border-amber-500";
  return "";
}

function SketchVariableStatusPill({ status }) {
  if (!status) return "—";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${status.pillClass}`}>
      <span className={`mr-1 h-2 w-2 shrink-0 rounded-full ${status.dotClass}`} />
      {status.label}
    </span>
  );
}

function SketchVariableTable({ typeGroup, variables, lsSuffix, t, onOpen }) {
  const showPreColumn = variables.some(variableHasPre);
  const colCount = showPreColumn ? 5 : 4;
  const unitHint =
    typeGroup.measurement_unit?.title || typeGroup.measurement_unit?.short_title || "";

  const rowProps = (v) => ({
    role: "button",
    tabIndex: 0,
    onClick: () => onOpen(v),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(v);
      }
    },
  });

  const emptyLabel = t("report.variables.sketch.noSubvariablesYet", {
    defaultValue: "Ingen variabler ennå",
  });

  return (
    <>
      <div className="md:hidden divide-y divide-gray-100">
        {variables.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">{emptyLabel}</div>
        ) : (
          variables.map((v) => {
            const { projCell, preCell, measCell } = sketchVariableRowCells(typeGroup, v);
            const status = sketchVariableStatusMeta(typeGroup, v, t);
            const showPre = variableHasPre(v);
            const accent = sketchVariableAccentClass(status);
            return (
              <div
                key={v.id}
                {...rowProps(v)}
                style={sketchVariableRowStyle(status)}
                className={`${sketchVariableRowClass(status)} px-4 py-4 sm:px-6 ${accent}`.trim()}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{v.reference || "—"}</div>
                    {unitHint ? <div className="text-xs text-gray-500">{unitHint}</div> : null}
                  </div>
                  <div className="shrink-0">
                    <SketchVariableStatusPill status={status} />
                  </div>
                </div>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-gray-500">
                      {t("report.variables.sketch.tableColProj", { defaultValue: "Proj." })} {lsSuffix}
                    </dt>
                    <dd className="font-medium text-gray-900">{projCell}</dd>
                  </div>
                  {showPre ? (
                    <div className="flex items-baseline justify-between gap-4">
                      <dt className="text-gray-500">
                        {t("report.variables.sketch.tableColPre", { defaultValue: "Pre" })} {lsSuffix}
                      </dt>
                      <dd className="font-medium text-amber-600">{preCell}</dd>
                    </div>
                  ) : null}
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-emerald-700">
                      {t("report.variables.sketch.tableColMeas", { defaultValue: "Målt" })} {lsSuffix}
                    </dt>
                    <dd className="font-medium text-emerald-600">{measCell}</dd>
                  </div>
                </dl>
              </div>
            );
          })
        )}
      </div>

      <div className="hidden md:block">
        <table className="w-full border-collapse">
          <thead className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left sm:px-6 sm:py-4">
                {t("report.variables.sketch.tableColVariable", { defaultValue: "Variabel" })}
              </th>
              <th className="px-4 py-3 text-right sm:px-6 sm:py-4">
                {t("report.variables.sketch.tableColProj", { defaultValue: "Proj." })} {lsSuffix}
              </th>
              {showPreColumn ? (
                <th className="px-4 py-3 text-right sm:px-6 sm:py-4">
                  {t("report.variables.sketch.tableColPre", { defaultValue: "Pre" })} {lsSuffix}
                </th>
              ) : null}
              <th className="px-4 py-3 text-right sm:px-6 sm:py-4">
                {t("report.variables.sketch.tableColMeas", { defaultValue: "Målt" })} {lsSuffix}
              </th>
              <th className="w-28 px-4 py-3 sm:w-32 sm:px-6 sm:py-4" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {variables.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-6 py-6 text-center text-sm text-gray-500">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              variables.map((v) => {
                const { projCell, preCell, measCell } = sketchVariableRowCells(typeGroup, v);
                const status = sketchVariableStatusMeta(typeGroup, v, t);
                const accent = sketchVariableAccentClass(status);
                return (
                  <tr
                    key={v.id}
                    {...rowProps(v)}
                    style={sketchVariableRowStyle(status)}
                    className={`group ${sketchVariableRowClass(status)}`}
                  >
                    <td
                      style={sketchVariableRowStyle(status)}
                      className={sketchVariableCellClass(
                        status,
                        `px-4 py-4 align-middle sm:px-6 sm:py-5${accent ? ` ${accent}` : ""}`
                      )}
                    >
                      <div className="font-medium text-gray-900">{v.reference || "—"}</div>
                      {unitHint ? <div className="text-xs text-gray-500">{unitHint}</div> : null}
                    </td>
                    <td
                      style={sketchVariableRowStyle(status)}
                      className={sketchVariableCellClass(
                        status,
                        "px-4 py-4 text-right align-middle text-sm font-medium text-gray-900 sm:px-6 sm:py-5"
                      )}
                    >
                      {projCell}
                    </td>
                    {showPreColumn ? (
                      <td
                        style={sketchVariableRowStyle(status)}
                        className={sketchVariableCellClass(
                          status,
                          `px-4 py-4 text-right align-middle text-sm font-medium sm:px-6 sm:py-5 ${
                            preCell !== "—" ? "text-amber-800" : "text-gray-500"
                          }`
                        )}
                      >
                        {preCell}
                      </td>
                    ) : null}
                    <td
                      style={sketchVariableRowStyle(status)}
                      className={sketchVariableCellClass(
                        status,
                        "px-4 py-4 text-right align-middle text-sm font-medium text-emerald-900 sm:px-6 sm:py-5"
                      )}
                    >
                      {measCell}
                    </td>
                    <td
                      style={sketchVariableRowStyle(status)}
                      className={sketchVariableCellClass(
                        status,
                        "px-4 py-4 text-right align-middle sm:px-6 sm:py-5"
                      )}
                    >
                      <SketchVariableStatusPill status={status} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export const ReportVariablesGroupsContent = ({
  report,
  canEdit: canEditProp,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const collaboration = useReportVariableMutations(report?.id);
  const [collaborationContainer, setCollaborationContainer] = useState(null);
  const { createReportVariableGroup, updateReportVariableGroup, deleteReportVariableGroup, createReportVariable, updateReportVariable, deleteReportVariable, createReportVariableTypeGroup, updateReportVariableTypeGroup, deleteReportVariableTypeGroup, createReportVariableMeasurement } = collaboration.mutations;
  const reportUsesLegacy = reportUsesLegacyVariables(report);
  const [legacyVariables, setLegacyVariables] = useState([]);
  const legacyFormikRef = useRef(null);
  const [isSavingLegacyVariables, setIsSavingLegacyVariables] = useState(false);
  const [orphanedMainVariables, setOrphanedMainVariables] = useState([]);
  const [variableGroups, setVariableGroups] = useState([]);
  const [variableTypeGroups, setVariableTypeGroups] = useState([]);
  const [variables, setVariables] = useState([]);
  /** Avoid empty → hydrated UI flicker before first variables fetch per report. */
  const [variablesContentReady, setVariablesContentReady] = useState(false);
  const reportIdRef = useRef(null);
  reportIdRef.current = report?.id;

  // Dialog states
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [typeGroupDialogOpen, setTypeGroupDialogOpen] = useState(false);
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkAddVariablesDialogOpen, setBulkAddVariablesDialogOpen] = useState(false);
  const [bulkAddTypeGroup, setBulkAddTypeGroup] = useState(null);
  const [measurementDialogOpen, setMeasurementDialogOpen] = useState(false);
  const [selectedVariableForMeasurement, setSelectedVariableForMeasurement] = useState(null);
  const [sketchEditorOpen, setSketchEditorOpen] = useState(false);
  const [sketchEditorTarget, setSketchEditorTarget] = useState(null);

  // Form states
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingTypeGroup, setEditingTypeGroup] = useState(null);
  const editingTypeGroupRef = useRef(null);
  const [editingVariable, setEditingVariable] = useState(null);
  // Keep ref in sync with state
  useEffect(() => {
    editingTypeGroupRef.current = editingTypeGroup;
  }, [editingTypeGroup]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'group', 'typeGroup', 'variable'

  const [groupForm, setGroupForm] = useState({ title: "", description: "" });
  const [typeGroupForm, setTypeGroupForm] = useState({
    report_variable_type_id: null,
    variable_type: null,
    measurement_unit_id: null,
    measurement_unit: null,
    projected_unit_id: null,
    projected_unit: null,
  });
  const [variableForm, setVariableForm] = useState({
    reference: "",
    projected_quantity: "",
    pre_measurement_quantity: "",
    note: "",
  });

  const canEdit =
    canEditProp !== undefined
      ? canEditProp
      : report?.user_can_edit_with_rights !== false ||
        report?.user_can_edit !== false;

  // Load variables data function
  const loadVariablesData = async ({ refresh = false } = {}) => {
    const token = collaboration.refreshToken();
    const startId = report?.id;
    if (!startId) return;

    try {
      const [variablesResponse, groupsResponse, typeGroupsResponse] = await Promise.all([
        fetchReportVariables(startId),
        fetchReportVariableGroups(startId),
        fetchReportVariableTypeGroups(startId),
      ]);

      if (reportIdRef.current !== startId) return;

      const versions = [variablesResponse, groupsResponse, typeGroupsResponse].map(r => r?.report_edit_version);
      if (!versions[0] || versions.some(v => v !== versions[0])) {
        throw new Error("Rapporten ble endret under lasting. Last siden på nytt.");
      }
      const apply = () => {
      if (refresh) collaboration.replaceCleanSnapshot(versions[0], token);
      else collaboration.adoptSnapshot(versions[0]);
      const allVariables = variablesResponse?.data || [];
      if (reportUsesLegacy) {
        setLegacyVariables(allVariables);
        return;
      }
      const allGroups = groupsResponse?.data || [];
      const allTypeGroups = typeGroupsResponse?.data || [];

      // Separate legacy variables (old style with field1/field2, no group, no type_group, no measurements)
      const legacy = allVariables.filter(isLegacyStyleReportVariable);

      // Separate orphaned main variables (is_main_variable but no type_group_id)
      const orphanedMainVariables = allVariables.filter((variable) => {
        return variable.is_main_variable && !variable.type_group_id;
      });

      // New system variables (exclude legacy and orphaned)
      const newVariables = allVariables.filter((variable) => 
        !legacy.includes(variable) && !orphanedMainVariables.includes(variable)
      );

      setLegacyVariables(
        legacy.map((v) => ({
          ...v,
          field1: v.field_one || v.field1 || '',
          field2: v.field_two || v.field2 || '',
          variable_type: v.variable_type || (v.report_variable_type_id ? { id: v.report_variable_type_id } : null),
          unit: v.unit || (v.unit_id ? { id: v.unit_id } : null),
        }))
      );
      setOrphanedMainVariables(orphanedMainVariables);
      setVariableGroups(allGroups);
      setVariableTypeGroups(allTypeGroups);
      setVariables(newVariables);
      };
      if (refresh) return { apply };
      apply();
    } catch (error) {
      if (refresh) throw error;
      collaboration.fail(error);
    }
  };

  // Load variables when report or legacy mode changes; keep shell stable until first load completes.
  useEffect(() => {
    if (!report?.id) {
      setVariablesContentReady(false);
      return;
    }
    let cancelled = false;
    setVariablesContentReady(false);
    loadVariablesData()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setVariablesContentReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [report?.id, reportUsesLegacy, report?.created_at, report?.uses_legacy_variables]);

  // Helper functions
  const getMainVariableForTypeGroup = (typeGroupId) => {
    return variables.find(
      (v) => v.type_group_id === typeGroupId && v.is_main_variable
    );
  };

  const getSubVariablesForTypeGroup = (typeGroupId) => {
    return variables.filter(
      (v) => v.type_group_id === typeGroupId && !v.is_main_variable
    );
  };

  const getTypeGroupsForGroup = (groupId) => {
    // Find all type groups that have a main variable in this group
    return variableTypeGroups.filter((tg) => {
      const mainVar = getMainVariableForTypeGroup(tg.id);
      return mainVar && mainVar.group_id === groupId;
    });
  };

  const getTypeGroupsNotInGroup = () => {
    // Find all type groups that have a main variable without a group
    return variableTypeGroups.filter((tg) => {
      const mainVar = getMainVariableForTypeGroup(tg.id);
      return mainVar && !mainVar.group_id;
    });
  };

  // Group handlers
  const handleCreateGroup = async () => {
    if (!groupForm.title.trim()) {
      toast.error("Gruppenavn er påkrevd");
      return;
    }

    try {
      const response = await createReportVariableGroup(report.id, {
        title: groupForm.title,
        description: groupForm.description,
      });

      if (response.data) {
        toast.success("Gruppe opprettet");
        setGroupDialogOpen(false);
        setGroupForm({ title: "", description: "" });
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke opprette gruppe");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !groupForm.title.trim()) {
      toast.error("Gruppenavn er påkrevd");
      return;
    }

    try {
      const response = await updateReportVariableGroup(report.id, editingGroup.id, {
        title: groupForm.title,
        description: groupForm.description,
      });

      if (response.data) {
        toast.success("Gruppe oppdatert");
        setGroupDialogOpen(false);
        setEditingGroup(null);
        setGroupForm({ title: "", description: "" });
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke oppdatere gruppe");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  const handleDeleteGroup = async () => {
    if (!itemToDelete) return;

    try {
      const response = await deleteReportVariableGroup(report.id, itemToDelete.id);
      if (response.message || response.data) {
        toast.success("Gruppe slettet");
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setDeleteType(null);
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke slette gruppe");
        }
      } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  // Type Group handlers (Main Variables)
  const handleCreateTypeGroup = async () => {
    if (!typeGroupForm.report_variable_type_id || !typeGroupForm.measurement_unit_id) {
      toast.error("Variabeltype og måleenhet er påkrevd");
      return;
    }

    try {
      const response = await createReportVariableTypeGroup(report.id, {
        report_variable_type_id: typeGroupForm.report_variable_type_id,
        measurement_unit_id: typeGroupForm.measurement_unit_id,
        projected_unit_id: typeGroupForm.projected_unit_id,
      });

      if (response.data) {
        // Always create a main variable when creating a type group
        await createReportVariable(report.id, {
          reference: typeGroupForm.variable_type?.displayTitle || "",
          projected_quantity: null,
          projected_unit_id: typeGroupForm.projected_unit_id,
          measurement_unit_id: typeGroupForm.measurement_unit_id,
          variable_type_id: typeGroupForm.report_variable_type_id,
          type_group_id: response.data.id,
          group_id: editingGroup?.id || null,
          is_main_variable: true,
        });

        toast.success("Type gruppe opprettet");
        setTypeGroupDialogOpen(false);
        setTypeGroupForm({
          report_variable_type_id: null,
          variable_type: null,
          measurement_unit_id: null,
          measurement_unit: null,
          projected_unit_id: null,
          projected_unit: null,
        });
        setEditingGroup(null);
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke opprette type gruppe");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  const handleUpdateTypeGroup = async () => {
    if (!editingTypeGroup || !typeGroupForm.report_variable_type_id || !typeGroupForm.measurement_unit_id) {
      toast.error("Variabeltype og måleenhet er påkrevd");
        return;
      }

    try {
      const response = await updateReportVariableTypeGroup(report.id, editingTypeGroup.id, {
        report_variable_type_id: typeGroupForm.report_variable_type_id,
        measurement_unit_id: typeGroupForm.measurement_unit_id,
        projected_unit_id: typeGroupForm.projected_unit_id,
      });

      if (response.data) {
        toast.success("Type gruppe oppdatert");
        setTypeGroupDialogOpen(false);
        setEditingTypeGroup(null);
        setTypeGroupForm({
          report_variable_type_id: null,
          variable_type: null,
          measurement_unit_id: null,
          measurement_unit: null,
          projected_unit_id: null,
          projected_unit: null,
        });
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
    } else {
        toast.error(response.errors?.[0] || "Kunne ikke oppdatere type gruppe");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  const handleDeleteTypeGroup = async () => {
    if (!itemToDelete) return;

    try {
      const response = await deleteReportVariableTypeGroup(report.id, itemToDelete.id);
      if (response.message || response.data) {
        toast.success("Type gruppe slettet");
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setDeleteType(null);
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke slette type gruppe");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  // Variable handlers (Sub-variables)
  const handleCreateVariable = async () => {
    if (!variableForm.reference?.trim()) {
      toast.error("Referanse er påkrevd");
      return;
    }

    if (!editingTypeGroup) {
      toast.error("Type gruppe må være valgt");
      return;
    }

    const mainVariable = getMainVariableForTypeGroup(editingTypeGroup.id);
    if (!mainVariable) {
      toast.error("Type gruppe ikke funnet");
      return;
    }

    try {
      const response = await createReportVariable(report.id, {
        reference: variableForm.reference,
        projected_quantity: variableForm.projected_quantity || null,
        pre_measurement_quantity: variableForm.pre_measurement_quantity || null,
        note: variableForm.note || null,
        projected_unit_id: editingTypeGroup.projected_unit_id || null,
        measurement_unit_id: editingTypeGroup.measurement_unit_id,
        variable_type_id: editingTypeGroup.report_variable_type_id,
        type_group_id: editingTypeGroup.id,
        group_id: mainVariable.group_id || null,
        is_main_variable: false,
      });

      if (response.data) {
        toast.success("Variabel opprettet");
        setVariableDialogOpen(false);
        setVariableForm({ reference: "", projected_quantity: "", pre_measurement_quantity: "", note: "" });
        setEditingTypeGroup(null);
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke opprette variabel");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  const handleUpdateVariable = async () => {
    if (!editingVariable || !variableForm.reference?.trim()) {
      toast.error("Referanse er påkrevd");
      return;
    }

    try {
      const response = await updateReportVariable(report.id, editingVariable.id, {
        reference: variableForm.reference,
        projected_quantity: variableForm.projected_quantity || null,
        pre_measurement_quantity: variableForm.pre_measurement_quantity || null,
        note: variableForm.note || null,
      });

      if (response.data) {
        toast.success("Variabel oppdatert");
        setVariableDialogOpen(false);
        setEditingVariable(null);
        setVariableForm({ reference: "", projected_quantity: "", pre_measurement_quantity: "", note: "" });
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke oppdatere variabel");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  const handleDeleteVariable = async () => {
    if (!itemToDelete) return;

    try {
      const response = await deleteReportVariable(report.id, itemToDelete.id);
      if (response.message || response.data) {
        toast.success("Variabel slettet");
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setDeleteType(null);
        // Reload variables data
        await loadVariablesData();
        if (onRefresh) await onRefresh();
        } else {
        toast.error(response.errors?.[0] || "Kunne ikke slette variabel");
        }
      } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  const openTypeGroupDialog = (group = null) => {
    setEditingGroup(group);
    setEditingTypeGroup(null);
    setTypeGroupForm({
      title: "",
      description: "",
      report_variable_type_id: null,
      variable_type: null,
      measurement_unit_id: null,
      measurement_unit: null,
      projected_unit_id: null,
      projected_unit: null,
      projected_quantity: "",
    });
    setTypeGroupDialogOpen(true);
  };

  const openVariableDialog = (typeGroup) => {
    setEditingTypeGroup(typeGroup);
    setEditingVariable(null);
    setVariableForm({ reference: "", projected_quantity: "", pre_measurement_quantity: "" });
    setVariableDialogOpen(true);
  };

  const openBulkAddVariablesDialog = (typeGroup) => {
    setBulkAddTypeGroup(typeGroup);
    setBulkAddVariablesDialogOpen(true);
  };

  const handleBulkAddVariables = async (variablesToAdd) => {
    if (!bulkAddTypeGroup || variablesToAdd.length === 0) {
      return;
    }

      // Get main variable to get group_id
      const mainVariable = getMainVariableForTypeGroup(bulkAddTypeGroup.id);

      // Create all variables
      const createPromises = variablesToAdd.map((variable) =>
        createReportVariable(report.id, {
          ...variable,
          group_id: mainVariable?.group_id || null,
        })
      );

      await Promise.all(createPromises);

      // Reload variables data
      await loadVariablesData();
      if (onRefresh) await onRefresh();

      setBulkAddVariablesDialogOpen(false);
      setBulkAddTypeGroup(null);
  };

  const typeGroupsNotInGroup = useMemo(
    () => getTypeGroupsNotInGroup(),
    [variableTypeGroups, variables]
  );

  // Handle saving legacy variables (using the old ReportVariables component)
  const handleSaveLegacyVariables = async (variables) => {
    setIsSavingLegacyVariables(true);
    try {
      // Filter out deleted items and format variables for API
      const variablesToSave = variables
        .filter(v => !v.delete)
        .map(v => ({
          id: v.id,
          reference: v.reference || '',
          variable_type_id: v.variable_type?.id || null,
          unit_id: v.unit?.id || v.unit_id || null,
          field_one: v.field1 || null,
          field_two: v.field2 || null,
          note: v.note || null,
        }));

      // Get variables to delete
      const variablesToDelete = variables.filter(v => v.delete && v.id);

      // Delete variables first
      if (variablesToDelete.length > 0) {
        await Promise.all(
          variablesToDelete.map(v => deleteReportVariable(report.id, v.id))
        );
      }

      // Update or create variables
      const savePromises = variablesToSave.map(v => {
        if (v.id) {
          // Update existing
          return updateReportVariable(report.id, v.id, {
            reference: v.reference,
            variable_type_id: v.variable_type_id,
            unit_id: v.unit_id,
            field_one: v.field_one,
            field_two: v.field_two,
            note: v.note,
          });
        } else {
          // Create new
          return createReportVariable(report.id, {
            reference: v.reference,
            variable_type_id: v.variable_type_id,
            unit_id: v.unit_id,
            field_one: v.field_one,
            field_two: v.field_two,
            note: v.note,
          });
        }
      });

      await Promise.all(savePromises);

      // Reload variables data
      await loadVariablesData();
      if (onRefresh) await onRefresh();
      
      toast.success("Variabler lagret");
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    } finally {
      setIsSavingLegacyVariables(false);
    }
  };

  // Handle legacy variable field updates (direct save per row) - DEPRECATED, kept for compatibility
  const handleLegacyVariableFieldChange = async (variableId, field, value, isNewRow = false) => {
    try {
      // If it's a new row and reference is filled, create the variable
      if (isNewRow && field === 'reference' && value && value.trim() !== '') {
        const newVariable = {
          reference: value,
          variable_type_id: null,
          unit_id: null,
          field_one: null,
          field_two: null,
        };
        
        const response = await createReportVariable(report.id, {
          reference: newVariable.reference,
          variable_type_id: newVariable.variable_type_id,
          unit_id: newVariable.unit_id,
          field_one: newVariable.field_one,
          field_two: newVariable.field_two,
        });

        if (response.data) {
          // Remove the temporary row and add the new variable, plus a new empty row
          setLegacyVariables(prev => {
            const filtered = prev.filter(v => v.id !== variableId);
            const newVariable = {
              ...response.data,
              field1: response.data.field_one,
              field2: response.data.field_two,
            };
            return [...filtered, newVariable, {
              id: `temp-${Date.now()}`,
              reference: '',
              variable_type_id: null,
              unit_id: null,
              field1: '',
              field2: '',
              isNew: true,
            }];
          });
          toast.success("Variabel opprettet");
      return;
        } else {
          toast.error(response.errors?.[0] || "Kunne ikke opprette variabel");
      return;
    }
      }

      const variable = legacyVariables.find(v => v.id === variableId);
      if (!variable) return;

      const updateData = {
        reference: variable.reference,
        variable_type_id: variable.report_variable_type_id,
        unit_id: variable.unit_id,
        note: variable.note,
      };

      // Update the specific field
      if (field === 'field1') {
        updateData.field_one = value;
      } else if (field === 'field2') {
        updateData.field_two = value;
      } else if (field === 'reference') {
        updateData.reference = value;
      } else if (field === 'variable_type_id') {
        updateData.variable_type_id = value;
      } else if (field === 'unit_id') {
        updateData.unit_id = value;
      } else if (field === 'note') {
        updateData.note = value;
      }

      const response = await updateReportVariable(report.id, variableId, updateData);
      
      if (response.data) {
        // Update local state immediately without full refresh
        setLegacyVariables(prev => 
          prev.map(v => 
            v.id === variableId 
              ? { ...v, ...updateData, field1: updateData.field_one, field2: updateData.field_two }
              : v
          )
        );
        toast.success("Variabel oppdatert");
      } else {
        toast.error(response.errors?.[0] || "Kunne ikke oppdatere variabel");
      }
    } catch (error) {
      // The shared collaboration panel owns this failure; retain the draft.
    }
  };

  // Handle adding a new empty row
  const handleAddLegacyVariableRow = () => {
    // Add a temporary row with a temporary ID
    const tempId = `temp-${Date.now()}`;
    setLegacyVariables(prev => [...prev, {
      id: tempId,
      reference: '',
      variable_type_id: null,
      unit_id: null,
      field1: '',
      field2: '',
      isNew: true,
    }]);
  };

  const showLegacyVariables = reportUsesLegacy || legacyVariables.length > 0;
  const showNewSystem = !reportUsesLegacy;

  // Stable reference required: inline initialValues + enableReinitialize makes Formik reset on every
  // parent render and fights ReportVariables' setFieldValue sync → flicker / apparent infinite loop.
  const legacyVariablesFormikInitial = useMemo(
    () => ({
      variables: legacyVariables.map((v) => ({
        id: v.id,
        variable_type: v.variable_type || null,
        reference: v.reference || "",
        field1: v.field1 || v.field_one || "",
        field2: v.field2 || v.field_two || "",
        unit: v.unit || null,
        unit_id: v.unit_id || null,
        note: v.note || "",
      })),
    }),
    [legacyVariables]
  );

  const sketchTotalsCards = useMemo(() => {
    if (!showNewSystem) return [];
    const map = aggregateTotalsByReportVariableTypeId(variables, variableTypeGroups);
    const rows = [];
    let idx = 0;
    for (const [, agg] of map.entries()) {
      const tg = agg.refTg;
      const title =
        getTypeGroupVariableTypeTitle(tg) ||
        t("report.variables.sketch.variableTypeFallback", { defaultValue: "Variabeltype" });
      if (!agg.hasProj && !agg.hasPre && !agg.hasMeas) continue;
      const dot = SKETCH_TOTAL_DOT_COLORS[idx % SKETCH_TOTAL_DOT_COLORS.length];
      idx += 1;
      const projPair = agg.hasProj
        ? formatFlowPairNb(tg.projected_unit || tg.measurement_unit, agg.projected)
        : { primary: null, secondary: null };
      const prePair = agg.hasPre
        ? formatFlowPairNb(tg.measurement_unit, agg.pre)
        : { primary: null, secondary: null };
      const measPair = agg.hasMeas
        ? formatFlowPairNb(tg.measurement_unit, agg.measured)
        : { primary: null, secondary: null };
      rows.push({
        key: String(tg.report_variable_type_id ?? tg.id),
        title,
        dot,
        projPair,
        prePair,
        measPair,
      });
    }
    rows.sort((a, b) => String(a.title).localeCompare(String(b.title), "nb"));
    return rows;
  }, [showNewSystem, variables, variableTypeGroups, t]);

  const legacySection = showLegacyVariables && (
    <div className="border-t border-gray-200 pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {reportUsesLegacy
            ? t("report.variables.legacy.headingOnly", { defaultValue: "Variabler" })
            : t("report.variables.legacy.headingTagged", { defaultValue: "Variabler (Legacy)" })}
        </h3>
        {isSavingLegacyVariables && (
          <div className="flex items-center text-sm text-gray-600">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {t("report.variables.legacy.saving", { defaultValue: "Lagrer..." })}
          </div>
        )}
      </div>
      <Formik
        innerRef={legacyFormikRef}
        initialValues={legacyVariablesFormikInitial}
        enableReinitialize={true}
        onSubmit={async (values) => {
          await handleSaveLegacyVariables(values.variables);
        }}
      >
        {(formik) => (
          <Form>
            <ReportVariables
              setFieldValue={formik.setFieldValue}
              values={formik.values}
              report={report}
              disabled={!canEdit}
            />
            {canEdit && (
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={formik.submitForm}
                  disabled={isSavingLegacyVariables}
                  className={`${RV_BTN_PRIMARY_DARK} !min-h-11 w-full text-base sm:w-auto sm:max-w-xs sm:text-sm`}
                >
                  {isSavingLegacyVariables
                    ? t("report.variables.legacy.saving", { defaultValue: "Lagrer..." })
                    : t("report.variables.legacy.save", { defaultValue: "Lagre variabler" })}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );

  const orphanedBlock =
    orphanedMainVariables.length > 0 ? (
      <div className="space-y-4 mb-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <h4 className="text-sm font-semibold text-yellow-800 mb-2">
            {t("report.variables.orphaned.title", { defaultValue: "Ufullstendige hovedvariabler" })}
          </h4>
          <p className="text-xs text-yellow-700 mb-3">
            {t("report.variables.orphaned.body", {
              defaultValue:
                "Disse variablene er markert som hovedvariabler men mangler type gruppe. De bør knyttes til en type gruppe eller slettes.",
            })}
          </p>
          <div className="space-y-2">
            {orphanedMainVariables.map((variable) => (
              <div
                key={variable.id}
                className="flex items-center justify-between p-2 bg-white border border-yellow-300 rounded"
              >
                <div>
                  <div className="text-sm font-medium">
                    ID: {variable.id} - {variable.reference || "Ingen referanse"}
                  </div>
                  <div className="text-xs text-gray-500">
                    Variable Type: {variable.variable_type?.displayTitle || "Ingen type"} | Unit:{" "}
                    {formatUnitDisplay(variable.unit)}
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setItemToDelete(variable);
                      setDeleteType("variable");
                      setDeleteDialogOpen(true);
                    }}
                    className={iconBtnDanger}
                    aria-label="Slett variabel"
                  >
                    <TrashIcon className="h-6 w-6 sm:h-5 sm:w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    ) : null;

  const openSketchVariableEditor = (variable, typeGroup, parentGroup) => {
    setSketchEditorTarget({ variable, typeGroup, group: parentGroup || null });
    setSketchEditorOpen(true);
  };

  const handleSketchFabAdd = () => {
    for (const g of variableGroups) {
      const tgs = getTypeGroupsForGroup(g.id);
      if (tgs.length > 0) {
        openVariableDialog(tgs[0]);
        return;
      }
    }
    if (typeGroupsNotInGroup.length > 0) {
      openVariableDialog(typeGroupsNotInGroup[0]);
      return;
    }
    toast.error(
      t("report.variables.sketch.fabNoType", {
        defaultValue: "Opprett en type-gruppe før du legger til variabler.",
      })
    );
  };

  const sketchNewSystemLayout =
    showNewSystem ? (
      <>
        <div className="space-y-10 pb-24 md:pb-0">
          {sketchTotalsCards.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {sketchTotalsCards.map((card) => (
                <div
                  key={card.key}
                  className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <span className={`h-3 w-3 shrink-0 rounded-full ${card.dot}`} />
                    {t("report.variables.sketch.cardTotalPrefix", { defaultValue: "TOTAL" })} {card.title}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between gap-4 text-sm sm:text-base">
                      <span className="text-gray-600">
                        {t("report.variables.sketch.totalsProjRow", { defaultValue: "Proj. total" })}
                      </span>
                      <span className="text-right font-medium">
                        {card.projPair.primary ? (
                          <>
                            {card.projPair.primary}
                            {card.projPair.secondary ? (
                              <span className="text-gray-400"> ({card.projPair.secondary})</span>
                            ) : null}
                          </>
                        ) : (
                          "—"
                        )}
                      </span>
                    </div>
                    {card.prePair.primary ? (
                      <div className="flex justify-between gap-4 text-sm sm:text-base">
                        <span className="text-gray-600">
                          {t("report.variables.sketch.totalsPreRow", { defaultValue: "Pre total" })}
                        </span>
                        <span className="text-right font-medium">
                          {card.prePair.primary}
                          {card.prePair.secondary ? (
                            <span className="text-gray-400"> ({card.prePair.secondary})</span>
                          ) : null}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex justify-between gap-4 border-t border-gray-200 pt-3 text-sm sm:text-base">
                      <span className="font-medium text-emerald-600">
                        {t("report.variables.sketch.totalsMeasRow", { defaultValue: "Målt total" })}
                      </span>
                      <span className="text-right font-medium text-emerald-600">
                        {card.measPair.primary ? (
                          <>
                            {card.measPair.primary}
                            {card.measPair.secondary ? (
                              <span className="text-emerald-800/70"> ({card.measPair.secondary})</span>
                            ) : null}
                          </>
                        ) : (
                          "—"
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {t("report.variables.groups.heading", { defaultValue: "Grupper" })}
              </h2>
              {canEdit ? (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    className={`${RV_BTN_OUTLINE} !min-h-11 flex items-center justify-center gap-2 rounded-xl text-sm font-medium`}
                    onClick={() => {
                      setEditingGroup(null);
                      setGroupForm({ title: "", description: "" });
                      setGroupDialogOpen(true);
                    }}
                  >
                    <PlusCircleIcon className="h-5 w-5 shrink-0" />
                    {t("report.variables.sketch.newGroup", { defaultValue: "Ny gruppe" })}
                  </button>
                  <button
                    type="button"
                    className={`${RV_BTN_PRIMARY_DARK} !min-h-11 flex items-center justify-center gap-2 rounded-xl text-sm font-medium`}
                    onClick={() => openTypeGroupDialog(null)}
                  >
                    <RectangleStackIcon className="h-5 w-5 shrink-0" />
                    {t("report.variables.sketch.typeGroupUngrouped", {
                      defaultValue: "Type-gruppe (uten gruppe)",
                    })}
                  </button>
                </div>
              ) : null}
            </div>

            {variableGroups.length === 0 && typeGroupsNotInGroup.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t("report.variables.sketch.emptyAll", {
                  defaultValue:
                    "Ingen grupper eller løse variabeltyper ennå. Bruk knappene over for å opprette.",
                })}
              </p>
            ) : null}

            {variableGroups.map((group) => {
              const typeGs = getTypeGroupsForGroup(group.id);
              return (
                <div
                  key={group.id}
                  className="mb-6 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
                    <div className="flex min-w-0 items-center gap-3">
                      <RectangleStackIcon className="h-6 w-6 shrink-0 text-gray-600" aria-hidden />
                      <div className="min-w-0">
                        <span className="font-semibold text-gray-900">
                          {group.title?.trim() ||
                            t("report.variables.sketch.untitledGroup", { defaultValue: "Uten navn" })}
                        </span>
                        {typeGs.length > 0 ? (
                          <span className="mt-1 block text-sm text-gray-500 sm:ml-3 sm:mt-0 sm:inline">
                            {typeGs
                              .map((tg) => getTypeGroupVariableTypeTitle(tg))
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {canEdit ? (
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className={`${RV_BTN_OUTLINE} !min-h-10 rounded-xl px-3 text-sm inline-flex items-center justify-center gap-1.5`}
                          onClick={() => openTypeGroupDialog(group)}
                        >
                          <PlusCircleIcon className="h-5 w-5 shrink-0" />
                          {t("report.variables.sketch.addMainVariable", { defaultValue: "Hovedvariabel" })}
                        </button>
                        <button
                          type="button"
                          className="rounded-lg px-3 py-1.5 text-sm hover:bg-white"
                          onClick={() => {
                            setEditingGroup(group);
                            setGroupForm({
                              title: group.title || "",
                              description: group.description || "",
                            });
                            setGroupDialogOpen(true);
                          }}
                        >
                          {t("report.variables.sketch.editGroup", { defaultValue: "Rediger" })}
                        </button>
                        <button
                          type="button"
                          className={`${iconBtnDanger} !h-10 !w-10 sm:!h-9 sm:!w-9`}
                          onClick={() => {
                            setItemToDelete(group);
                            setDeleteType("group");
                            setDeleteDialogOpen(true);
                          }}
                          aria-label={t("report.variables.sketch.deleteGroupAria", {
                            defaultValue: "Slett gruppe",
                          })}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {typeGs.length === 0 ? (
                    <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
                      <p className="text-sm text-gray-500">
                        {t("report.variables.sketch.groupTabEmpty", {
                          defaultValue: "Ingen hovedvariabler her ennå.",
                        })}
                      </p>
                      {canEdit ? (
                        <button
                          type="button"
                          className={`${RV_BTN_PRIMARY_DARK} !min-h-10 rounded-xl px-4 text-sm font-medium inline-flex items-center justify-center gap-2`}
                          onClick={() => openTypeGroupDialog(group)}
                        >
                          <PlusCircleIcon className="h-5 w-5 shrink-0" />
                          {t("report.variables.sketch.addMainVariable", { defaultValue: "Hovedvariabel" })}
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <>
                    {typeGs.map((tg) => {
                      const mainVar = getMainVariableForTypeGroup(tg.id);
                      const subVariables = getSubVariablesForTypeGroup(tg.id).filter(
                        (v) =>
                          v.group_id === group.id ||
                          (mainVar?.group_id === group.id &&
                            (v.group_id == null || v.group_id === group.id))
                      );
                      const flowCols =
                        getFlowRateUnitKind(tg.measurement_unit) ||
                        getFlowRateUnitKind(tg.projected_unit || tg.measurement_unit);
                      const lsSuffix = flowCols
                        ? t("report.variables.sketch.tableColLsSuffix", { defaultValue: "(l/s)" })
                        : "";
                      return (
                        <div key={tg.id}>
                          <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-white px-4 py-2 sm:px-6">
                            <span className="text-sm font-medium text-gray-700">
                              {getTypeGroupVariableTypeTitle(tg) ||
                                t("report.variables.sketch.variableTypeFallback", {
                                  defaultValue: "Variabeltype",
                                })}
                            </span>
                            {canEdit ? (
                              <div className="flex shrink-0 items-center gap-1">
                                <button
                                  type="button"
                                  className={iconBtnAccent}
                                  aria-label={t("report.variables.sketch.editTypeGroupAria", {
                                    defaultValue: "Rediger variabeltype",
                                  })}
                                  onClick={() => {
                                    setEditingTypeGroup(tg);
                                    setEditingGroup(group);
                                    setTypeGroupForm({
                                      report_variable_type_id: tg.report_variable_type_id,
                                      variable_type: normalizeVariableType(tg.report_variable_type),
                                      measurement_unit_id: tg.measurement_unit_id,
                                      measurement_unit: tg.measurement_unit,
                                      projected_unit_id: tg.projected_unit_id,
                                      projected_unit: tg.projected_unit,
                                    });
                                    setTypeGroupDialogOpen(true);
                                  }}
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  type="button"
                                  className={iconBtnDanger}
                                  aria-label={t("report.variables.sketch.deleteTypeGroupAria", {
                                    defaultValue: "Slett variabeltype",
                                  })}
                                  onClick={() => {
                                    setItemToDelete(tg);
                                    setDeleteType("typeGroup");
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            ) : null}
                          </div>
                          <SketchVariableTable
                            typeGroup={tg}
                            variables={subVariables}
                            lsSuffix={lsSuffix}
                            t={t}
                            onOpen={(v) => openSketchVariableEditor(v, tg, group)}
                          />
                          {canEdit ? (
                            <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row">
                              <button
                                type="button"
                                className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium hover:bg-gray-50 sm:flex-none sm:px-6"
                                onClick={() => openVariableDialog(tg)}
                              >
                                <PlusCircleIcon className="h-5 w-5" />
                                {t("report.variables.sketch.addVariable", { defaultValue: "Legg til variabel" })}
                              </button>
                              <button
                                type="button"
                                className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-gray-200 py-3 text-sm font-medium hover:bg-gray-50 sm:flex-none sm:px-6"
                                onClick={() => openBulkAddVariablesDialog(tg)}
                              >
                                {t("report.variables.sketch.bulkAddShort", { defaultValue: "Bulk-add" })}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    {canEdit ? (
                      <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
                        <button
                          type="button"
                          className={`${RV_BTN_OUTLINE} w-full rounded-2xl sm:w-auto inline-flex items-center justify-center gap-2`}
                          onClick={() => openTypeGroupDialog(group)}
                        >
                          <PlusCircleIcon className="h-5 w-5 shrink-0" />
                          {t("report.variables.sketch.addAnotherMainVariable", {
                            defaultValue: "Legg til ny hovedvariabel i gruppen",
                          })}
                        </button>
                      </div>
                    ) : null}
                    </>
                  )}
                </div>
              );
            })}

            {typeGroupsNotInGroup.length > 0 ? (
              <div className="mb-6 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <RectangleStackIcon className="h-6 w-6 text-gray-600" aria-hidden />
                    <span className="font-semibold text-gray-900">
                      {t("report.variables.sketch.ungroupedTab", { defaultValue: "Uten gruppe" })}
                    </span>
                  </div>
                </div>
                {typeGroupsNotInGroup.map((tg) => {
                  const subVariables = getSubVariablesForTypeGroup(tg.id).filter((v) => !v.group_id);
                  const flowCols =
                    getFlowRateUnitKind(tg.measurement_unit) ||
                    getFlowRateUnitKind(tg.projected_unit || tg.measurement_unit);
                  const lsSuffix = flowCols
                    ? t("report.variables.sketch.tableColLsSuffix", { defaultValue: "(l/s)" })
                    : "";
                  return (
                    <div key={tg.id}>
                      <div className="border-b border-gray-100 bg-white px-4 py-2 text-sm font-medium text-gray-700 sm:px-6">
                        {getTypeGroupVariableTypeTitle(tg) ||
                          t("report.variables.sketch.variableTypeFallback", {
                            defaultValue: "Variabeltype",
                          })}
                      </div>
                      <SketchVariableTable
                        typeGroup={tg}
                        variables={subVariables}
                        lsSuffix={lsSuffix}
                        t={t}
                        onOpen={(v) => openSketchVariableEditor(v, tg, null)}
                      />
                      {canEdit ? (
                        <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row">
                          <button
                            type="button"
                            className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium hover:bg-gray-50 sm:flex-none sm:px-6"
                            onClick={() => openVariableDialog(tg)}
                          >
                            <PlusCircleIcon className="h-5 w-5" />
                            {t("report.variables.sketch.addVariable", { defaultValue: "Legg til variabel" })}
                          </button>
                          <button
                            type="button"
                            className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-gray-200 py-3 text-sm font-medium hover:bg-gray-50 sm:flex-none sm:px-6"
                            onClick={() => openBulkAddVariablesDialog(tg)}
                          >
                            {t("report.variables.sketch.bulkAddShort", { defaultValue: "Bulk-add" })}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          {orphanedMainVariables.length > 0 ? <div className="mt-8 space-y-4">{orphanedBlock}</div> : null}
          {showLegacyVariables ? <div className="mt-8">{legacySection}</div> : null}
        </div>

        <VariableSketchEditorDialog
        collaborationAnchor={setCollaborationContainer}
          mutations={collaboration.mutations}
          open={sketchEditorOpen}
          onClose={() => {
            setSketchEditorOpen(false);
            setSketchEditorTarget(null);
          }}
          variable={sketchEditorTarget?.variable}
          typeGroup={sketchEditorTarget?.typeGroup}
          group={sketchEditorTarget?.group}
          report={report}
          canEdit={canEdit}
          onSaved={loadVariablesData}
        />

        {canEdit ? (
          <button
            type="button"
            className="fixed right-6 z-[3400] flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-900 text-3xl font-light leading-none text-white shadow-xl transition hover:bg-gray-800 active:scale-95 md:hidden bottom-[calc(var(--mobile-nav-height,3.75rem)+1.5rem)]"
            onClick={handleSketchFabAdd}
            aria-label={t("report.variables.sketch.addVariable", { defaultValue: "Legg til variabel" })}
          >
            +
          </button>
        ) : null}
      </>
    ) : null;

  return (
    <div className="w-full space-y-6">
      <RecordCollaborationPanel
        portalTarget={collaborationContainer}
        report={{ ...report, edit_version: collaboration.version || report?.edit_version }}
        section="variables"
        editing={groupDialogOpen || typeGroupDialogOpen || variableDialogOpen || bulkAddVariablesDialogOpen || measurementDialogOpen || sketchEditorOpen || isSavingLegacyVariables}
        saving={!variablesContentReady || collaboration.pending > 0 || deleteDialogOpen}
        canRefresh={() => !legacyFormikRef.current?.dirty && !legacyFormikRef.current?.isSubmitting}
        onRefresh={() => loadVariablesData({ refresh: true })}
        needsReview={Boolean(collaboration.problem)} errorMessage={collaboration.problem?.safeMessage}
        zIndex={3800} onReload={() => { collaboration.stop(); window.location.reload(); }}
      />
      {!variablesContentReady ? (
        <ResourceLoading size="base" />
      ) : (
        <>
          {sketchNewSystemLayout}

          {!showNewSystem && showLegacyVariables && legacySection}
        </>
      )}

      {/* Group Dialog */}
      <GroupDialog
        collaborationAnchor={setCollaborationContainer}
        isOpen={groupDialogOpen}
        onClose={() => {
          setGroupDialogOpen(false);
          setEditingGroup(null);
          setGroupForm({ title: "", description: "" });
        }}
        group={editingGroup}
        form={groupForm}
        setForm={setGroupForm}
        onSave={editingGroup ? handleUpdateGroup : handleCreateGroup}
        canEdit={canEdit}
      />

      {/* Type Group Dialog */}
      <TypeGroupDialog
        collaborationAnchor={setCollaborationContainer}
        isOpen={typeGroupDialogOpen}
        onClose={() => {
          setTypeGroupDialogOpen(false);
    setEditingTypeGroup(null);
    setEditingGroup(null);
    setTypeGroupForm({
      report_variable_type_id: null,
      variable_type: null,
      measurement_unit_id: null,
      measurement_unit: null,
      projected_unit_id: null,
      projected_unit: null,
      projected_quantity: "",
    });
        }}
        typeGroup={editingTypeGroup}
        form={typeGroupForm}
        setForm={setTypeGroupForm}
        onSave={() => {
          // Use ref to ensure we have the correct value even if state changed
          const currentEditingTypeGroup = editingTypeGroupRef.current;
          if (currentEditingTypeGroup) {
            handleUpdateTypeGroup();
            } else {
            handleCreateTypeGroup();
          }
        }}
        canEdit={canEdit}
      />

      {/* Variable Dialog */}
      <VariableDialog
        collaborationAnchor={setCollaborationContainer}
        isOpen={variableDialogOpen}
        onClose={() => {
          setVariableDialogOpen(false);
          setEditingVariable(null);
          setEditingTypeGroup(null);
          setVariableForm({ reference: "", projected_quantity: "", pre_measurement_quantity: "", note: "" });
        }}
        variable={editingVariable}
        typeGroup={editingTypeGroup}
        form={variableForm}
        setForm={setVariableForm}
        onSave={editingVariable ? handleUpdateVariable : handleCreateVariable}
        canEdit={canEdit}
      />

      {/* Bulk Add Variables Dialog */}
      <BulkAddVariablesDialog
        collaborationAnchor={setCollaborationContainer}
        open={bulkAddVariablesDialogOpen}
        onClose={() => {
          setBulkAddVariablesDialogOpen(false);
          setBulkAddTypeGroup(null);
        }}
        onSave={handleBulkAddVariables}
        typeGroup={bulkAddTypeGroup}
        report={report}
        canEdit={canEdit}
      />

      {/* Variable Measurement Dialog */}
      <VariableMeasurementDialog
        collaborationAnchor={setCollaborationContainer}
        mutations={collaboration.mutations}
        open={measurementDialogOpen}
        onClose={() => {
          setMeasurementDialogOpen(false);
          setSelectedVariableForMeasurement(null);
        }}
        variable={selectedVariableForMeasurement}
        report={report}
        canEdit={canEdit}
        onMeasurementAdded={async () => {
          // Reload variables data to get updated measurements
          await loadVariablesData();
          if (onRefresh) await onRefresh();
        }}
      />

      {/* Delete Confirmation */}
      <ProConfirmation
        open={deleteDialogOpen}
        cancelCallback={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
          setDeleteType(null);
        }}
        yesCallback={() => {
          if (deleteType === "group") {
            handleDeleteGroup();
          } else if (deleteType === "typeGroup") {
            handleDeleteTypeGroup();
          } else if (deleteType === "variable") {
            handleDeleteVariable();
          }
          setDeleteDialogOpen(false);
          setItemToDelete(null);
          setDeleteType(null);
        }}
        action="delete"
        severity="confirmation"
        message={
          deleteType === "group"
            ? `Er du sikker på at du vil slette gruppen "${itemToDelete?.title}"?`
            : deleteType === "typeGroup"
            ? `Er du sikker på at du vil slette type gruppen? Dette vil også slette alle under-variabler.`
            : `Er du sikker på at du vil slette variabelen "${itemToDelete?.reference}"?`
        }
      />
    </div>
  );
};


// Group Dialog Component
const GroupDialog = ({ collaborationAnchor, isOpen, onClose, group, form, setForm, onSave, canEdit }) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className={RV_DIALOG_Z} onClose={onClose}>
        <Transition.Child as={Fragment} {...rvTransitionOverlay}>
          <div className={RV_OVERLAY_CLASS} />
        </Transition.Child>

        <div className={RV_CENTER_WRAPPER}>
          <div className={RV_CENTER_INNER}>
            <Transition.Child as={Fragment} {...rvTransitionPanel}>
              <Dialog.Panel className={`${RV_PANEL_MD} !transform-none`}>
                <div ref={collaborationAnchor} />
                <Dialog.Title className={`mb-1 ${RV_DIALOG_TITLE}`}>
                  {group ? "Rediger gruppe" : "Opprett gruppe"}
                </Dialog.Title>

                <div className="space-y-4">
                  <PMField
                    label="Gruppenavn"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    disabled={!canEdit}
                    formik={false}
                    required
                  />
                  <PMField
                    label="Beskrivelse"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    disabled={!canEdit}
                    formik={false}
                    multiline
                  />
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end sm:gap-3">
                  <button type="button" className={`${RV_BTN_OUTLINE} !min-h-11 w-full sm:w-auto`} onClick={onClose}>
                    Avbryt
                  </button>
                  <button type="button" className={`${RV_BTN_PRIMARY_DARK} !min-h-11 w-full sm:w-auto`} onClick={onSave}>
                    {group ? "Oppdater" : "Opprett"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Type Group Dialog Component
const TypeGroupDialog = ({ collaborationAnchor, isOpen, onClose, typeGroup, form, setForm, onSave, canEdit }) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className={RV_DIALOG_Z} onClose={onClose}>
        <Transition.Child as={Fragment} {...rvTransitionOverlay}>
          <div className={RV_OVERLAY_CLASS} />
        </Transition.Child>

        <div className={RV_CENTER_WRAPPER}>
          <div className={RV_CENTER_INNER}>
            <Transition.Child as={Fragment} {...rvTransitionPanel}>
              <Dialog.Panel className={`${RV_PANEL_LG} !transform-none`}>
                <div ref={collaborationAnchor} />
                <Dialog.Title className={`mb-4 ${RV_DIALOG_TITLE}`}>
                  {typeGroup ? "Rediger type gruppe" : "Opprett type gruppe"}
                </Dialog.Title>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <VariableTypeSelector
                      name="report_variable_type_id"
                      label="Variabeltype"
                      selected={form.variable_type}
                      zIndex={RV_SELECTOR_OVER_DIALOG_Z}
                      onChange={(e) => {
                        const raw = e.variable_type;
                        const normalized = normalizeVariableType(raw);
                        setForm({
                          ...form,
                          report_variable_type_id:
                            e.target?.value ?? normalized?.id ?? null,
                          variable_type: normalized,
                        });
                      }}
                    />
                    <UnitCustomSelector
                      label="Måleenhet"
                      selected={form.measurement_unit}
                      onChange={(e) => {
                        const unitId = e.target?.value || e.item?.id || e.value;
                        const unitObject = e.target?.object || e.item || e.target?.item;
                        setForm({
                          ...form,
                          measurement_unit_id: unitId,
                          measurement_unit: unitObject,
                        });
                      }}
                      disabled={!canEdit}
                      formik={false}
                      zIndex={RV_SELECTOR_OVER_DIALOG_Z}
                      required
                    />
                    <UnitCustomSelector
                      label="Prosjektert enhet (valgfritt)"
                      selected={form.projected_unit}
                      onChange={(e) => {
                        const unitId = e.target?.value || e.item?.id || e.value;
                        const unitObject = e.target?.object || e.item || e.target?.item;
                        setForm({
                          ...form,
                          projected_unit_id: unitId,
                          projected_unit: unitObject,
                        });
                      }}
                      disabled={!canEdit}
                      formik={false}
                      zIndex={RV_SELECTOR_OVER_DIALOG_Z}
                    />
                  </div>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end sm:gap-3">
                  <button type="button" className={`${RV_BTN_OUTLINE} !min-h-11 w-full sm:w-auto`} onClick={onClose}>
                    Avbryt
                  </button>
                  <button type="button" className={`${RV_BTN_PRIMARY_DARK} !min-h-11 w-full sm:w-auto`} onClick={onSave}>
                    {typeGroup ? "Oppdater" : "Opprett"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Variable Dialog Component
const VariableDialog = ({ collaborationAnchor, isOpen, onClose, variable, typeGroup, form, setForm, onSave, canEdit }) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className={RV_DIALOG_Z} onClose={onClose}>
        <Transition.Child as={Fragment} {...rvTransitionOverlay}>
          <div className={RV_OVERLAY_CLASS} />
        </Transition.Child>

        <div className={RV_CENTER_WRAPPER}>
          <div className={RV_CENTER_INNER}>
            <Transition.Child as={Fragment} {...rvTransitionPanel}>
              <Dialog.Panel className={`${RV_PANEL_MD} !transform-none`}>
                <div ref={collaborationAnchor} />
                <Dialog.Title className={`mb-4 ${RV_DIALOG_TITLE}`}>
                  {variable ? "Rediger variabel" : "Legg til variabel"}
                </Dialog.Title>

                <div className="space-y-4">
                  {typeGroup && (
                    <div className={RV_SECTION}>
                      <div className="mb-1 text-xs font-medium text-gray-500">Type gruppe</div>
                      <div className="text-sm text-gray-900">{getTypeGroupVariableTypeTitle(typeGroup) || "-"}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        Måleenhet: {formatUnitDisplay(typeGroup.measurement_unit)}
                      </div>
                      {typeGroup.projected_unit && (
                        <div className="mt-1 text-xs text-gray-500">
                          Prosjektert enhet: {formatUnitDisplay(typeGroup.projected_unit)}
                        </div>
                      )}
                    </div>
                  )}
                  <PMField
                    label="Referanse"
                    value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                    disabled={!canEdit}
                    formik={false}
                    required
                    placeholder="Referanse"
                  />
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <PMField
                      label="Prosjektert mengde (valgfritt)"
                      type="number"
                      value={form.projected_quantity}
                      onChange={(e) => setForm({ ...form, projected_quantity: e.target.value })}
                      disabled={!canEdit}
                      formik={false}
                      placeholder="Prosjektert mengde"
                    />
                    <PMField
                      label="Før-måling (valgfritt)"
                      type="number"
                      value={form.pre_measurement_quantity}
                      onChange={(e) => setForm({ ...form, pre_measurement_quantity: e.target.value })}
                      disabled={!canEdit}
                      formik={false}
                      placeholder="Før-måling"
                    />
                  </div>
                  {typeGroup &&
                    (typeGroup.projected_unit || typeGroup.measurement_unit) &&
                    form.projected_quantity !== "" &&
                    !isNaN(parseFloat(form.projected_quantity)) && (
                      <p className="text-xs text-gray-500">
                        {formatUnitDisplay(
                          typeGroup.projected_unit || typeGroup.measurement_unit,
                          form.projected_quantity
                        )}
                      </p>
                    )}
                  {typeGroup?.measurement_unit &&
                    form.pre_measurement_quantity !== "" &&
                    !isNaN(parseFloat(form.pre_measurement_quantity)) && (
                      <p className="text-xs text-gray-500">
                        {formatUnitDisplay(typeGroup.measurement_unit, form.pre_measurement_quantity)}
                      </p>
                    )}
                  <PMField
                    label="Notat"
                    type="textarea"
                    value={form.note || ""}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    disabled={!canEdit}
                    formik={false}
                    placeholder="Legg til notat..."
                    rows={3}
                  />
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end sm:gap-3">
                  <button type="button" className={`${RV_BTN_OUTLINE} !min-h-11 w-full sm:w-auto`} onClick={onClose}>
                    Avbryt
                  </button>
                  <button type="button" className={`${RV_BTN_PRIMARY_DARK} !min-h-11 w-full sm:w-auto`} onClick={onSave}>
                    {variable ? "Oppdater" : "Opprett"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
